import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  clientTextDraft,
  instanceOptionRefusal,
  legalActions,
  missingForRelease,
  stageQuery,
  type ClientText,
  type InquiryAction,
  type InquiryPage,
  type InquiryRecord,
  type InquiryStageFilter,
  type ReleaseChannel,
  openFirst,
} from "./inquiryQueue.types";
import { currentUserIdOf } from "../../../auth/userId";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** The slice of the RAW Feathers service the actions call. */
interface RawService {
  patch: (id: number, data: object, params?: object) => Promise<unknown>;
}

export const INQUIRY_QUEUE_PAGE_SIZE = 50;
/** "My questions" reads one person's questions in one page, so open-first holds across all of them. */
export const MY_QUESTIONS_PAGE_SIZE = 200;

export type InquiryListState = "idle" | "loading" | "error" | "unresolved" | "empty" | "rows";

/** The form open inside the detail dialog, if any. */
export interface ActionForm {
  action: InquiryAction;
  optionIds: string[];
  text: string;
  /** answer-client: who at the client answered, and when. */
  authorName: string;
  authorEmail: string;
  /** withdraw */
  reason: string;
  /** release */
  clientText: ClientText | null;
  detail: string;
  /** release: how it reaches the client. The person says; null until they do. */
  channel: ReleaseChannel | null;
  /**
   * release into the app: other clients the API found named in the text
   * (400 `other-client`), and whether the person confirmed those words are not
   * about them — the PDF's and the e-mail's rule, asked again because HQ itself
   * is now the one putting these words in front of the client.
   */
  othersFlagged: string[];
  othersConfirmed: boolean;
  /** assign ([hub] review round 1): the new assignee's email. */
  assigneeEmail: string;
}

const blankForm = (action: InquiryAction): ActionForm => ({
  action,
  optionIds: [],
  text: "",
  authorName: "",
  authorEmail: "",
  reason: "",
  clientText: null,
  detail: "",
  channel: null,
  othersFlagged: [],
  othersConfirmed: false,
  assigneeEmail: "",
});

/** Shown when an action's outcome could not be re-read: what is on screen may be out of date. */
export const STALE_AFTER_ACTION =
  "HQ could not reload this inquiry, so what you see may be out of date. Refresh the page before doing anything else with it.";

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * Clients → Inquiry Queue: the questions IntraSync has for its clients, from
 * the moment nobody inside could answer one until it is answered or withdrawn
 * (plan/2026-10-01_FROM-GANTRY_client-inquiries-contract.md).
 *
 * READS go through the `client-inquiries` ServiceStore (clients.stores.ts):
 * this store keeps only the ordered ids of the page on screen and resolves the
 * rows from that cache, so no API entity lives here.
 *
 * ACTIONS (answer / release / withdraw) go through the RAW Feathers client, as
 * InstanceUsersStore's do, for two reasons: the API answers an action with an
 * envelope (`{ inquiry, delivery }`), not an entity, which a ServiceStore would
 * cache as if it were one; and the raw client REJECTS on every refusal, so a
 * 403 or a 409 can never read as success. After an action the record is
 * re-read through the ServiceStore, which refreshes the cached row.
 *
 * Every action is a compare-and-set: it sends `expect` from the record on
 * screen. A 409 means someone else moved it first; the record is reloaded and
 * the person is told, never retried on their behalf.
 *
 * Release is offered only when the server says this person may release
 * (`mayRelease` on the list response — the API's own policy, not a copy of
 * its role list). The API re-checks on every call; this only hides a button.
 */
class InquiryQueueStore {
  rootStore: RootStore;

  pageIds: number[] = [];
  total: number | null = null;
  loading = false;
  loaded = false;
  error: string | null = null;
  mayRelease = false;
  /** [hub] review round 1: the server's hint that this person has operator/admin access. */
  mayAssign = false;

  stageFilter: InquiryStageFilter = "open";
  page = 0;
  pageSize = INQUIRY_QUEUE_PAGE_SIZE;
  /**
   * [hub] H3 — "My questions": the same queue, narrowed by the API to the
   * questions assigned to the signed-in person (`mine=true`), every stage,
   * open first. Set by its route; the Inquiry Queue instance never sets it.
   */
  mine = false;

  /** The record open in the dialog. */
  selectedId: number | null = null;
  form: ActionForm | null = null;
  submitting = false;
  actionError: string | null = null;
  /** A sentence after a successful action (e.g. that HQ did not publish). */
  notice: string | null = null;
  /** Page-level message, shown above the list (e.g. a dialog closed because its record could not be re-read). */
  banner: string | null = null;

  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- services ------------------------------------------------------------

  private get service(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      clientInquiriesStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("client-inquiries") ?? rs.clientInquiriesStore) as
        | ServiceStoreType
        | null
        | undefined) ?? null
    );
  }

  private get raw(): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service("client-inquiries") ?? null;
  }

  private get cached(): InquiryRecord[] {
    return (this.service?.entities ?? []) as unknown as InquiryRecord[];
  }

  // ---- derived -------------------------------------------------------------

  get rows(): InquiryRecord[] {
    const out: InquiryRecord[] = [];
    for (const id of this.pageIds) {
      const row = this.cached.find((r) => r.id === id);
      if (row) out.push(row);
    }
    return this.mine ? openFirst(out) : out;
  }

  /** Make this instance "My questions" (or not). Resets the filter to its mode's default. */
  setMine(mine: boolean) {
    this.mine = mine;
    this.pageSize = mine ? MY_QUESTIONS_PAGE_SIZE : INQUIRY_QUEUE_PAGE_SIZE;
    this.stageFilter = mine ? "all" : "open";
  }

  get selected(): InquiryRecord | null {
    if (this.selectedId === null) return null;
    return this.cached.find((r) => r.id === this.selectedId) ?? null;
  }

  get actions(): InquiryAction[] {
    return this.selected
      ? legalActions(this.selected, this.mayRelease, { userId: currentUserIdOf(this.rootStore), mayAssign: this.mayAssign })
      : [];
  }

  get listState(): InquiryListState {
    if (this.error) return "error";
    if (this.pageIds.length === 0) {
      if (this.loading) return "loading";
      if (!this.loaded) return "idle";
      return "empty";
    }
    if (this.rows.length < this.pageIds.length) return "unresolved";
    return "rows";
  }

  get query(): Record<string, unknown> {
    const query: Record<string, unknown> = { $limit: this.pageSize, $skip: this.page * this.pageSize };
    const stage = stageQuery(this.stageFilter);
    if (stage) query.stage = stage;
    if (this.mine) query.mine = true;
    return query;
  }

  /** What the release form still lacks, so Release is disabled with a reason rather than refused. */
  get releaseMissing(): string[] {
    const r = this.selected;
    if (!r || this.form?.action !== "release" || !this.form.clientText) return [];
    return missingForRelease(this.form.clientText, r.options);
  }

  /**
   * Why the selected question cannot go into the client's IntraSync app (it has
   * more options than the app takes), or null. The release form greys that
   * choice out and says this; the API refuses it anyway.
   */
  get appRefusal(): string | null {
    return this.selected ? instanceOptionRefusal(this.selected.options.length) : null;
  }

  get canSubmit(): boolean {
    const f = this.form;
    const r = this.selected;
    if (!f || !r || this.submitting) return false;
    switch (f.action) {
      case "answer-internal":
      case "answer-client":
        if (f.optionIds.length === 0 && !f.text.trim()) return false;
        return r.multi || f.optionIds.length <= 1;
      case "release":
        return (
          this.releaseMissing.length === 0 &&
          f.channel !== null &&
          !(f.channel === "instance" && this.appRefusal !== null) &&
          !(f.channel === "instance" && f.othersFlagged.length > 0 && !f.othersConfirmed)
        );
      case "client-text":
        // A draft may be saved incomplete: release, the PDF and the email each refuse what is missing.
        return f.clientText !== null;
      case "withdraw":
        return f.reason.trim().length > 0;
      case "assign":
        return /^[^\s@]+@[^\s@]+$/.test(f.assigneeEmail.trim());
      default:
        return false;
    }
  }

  // ---- loading -------------------------------------------------------------

  async initialize() {
    this.page = 0;
    await this.load();
  }

  async load() {
    const seq = ++this.loadSeq;
    const service = this.service;
    this.loading = true;
    this.error = null;
    if (!service) {
      this.loading = false;
      this.loaded = true;
      this.error = "The client-inquiries service is not available in this build.";
      return;
    }
    try {
      const result = (await service.find({ query: this.query, throwOnError: true } as never)) as unknown as InquiryPage | null;
      if (seq !== this.loadSeq) return;
      if (!result || !Array.isArray(result.data)) throw new Error("The server returned no inquiry list.");
      runInAction(() => {
        this.pageIds = result.data.map((r) => r.id);
        this.total = typeof result.total === "number" ? result.total : result.data.length;
        this.mayRelease = result.mayRelease === true;
        this.mayAssign = result.mayAssign === true;
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.pageIds = [];
        this.total = null;
        this.error = messageOf(err, "Failed to load client inquiries");
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
          this.loaded = true;
        });
      }
    }
  }

  setStageFilter(filter: InquiryStageFilter) {
    if (filter === this.stageFilter) return;
    this.stageFilter = filter;
    this.page = 0;
    this.pageIds = [];
    void this.load();
  }

  setPage(page: number) {
    this.page = Math.max(0, page);
    this.pageIds = [];
    void this.load();
  }

  // ---- the dialog ----------------------------------------------------------

  select(id: number | null) {
    this.selectedId = id;
    this.form = null;
    this.actionError = null;
    this.notice = null;
    // The question open in the dialog is the one its PDF and email are for (#4211, #17).
    this.delivery?.open(id);
  }

  /** The dialog's PDF and email panel (InquiryDeliveryStore), when this build has it. */
  private get delivery(): { open: (id: number | null) => void } | null {
    return (this.rootStore as unknown as { inquiryDeliveryStore?: { open: (id: number | null) => void } }).inquiryDeliveryStore ?? null;
  }

  /**
   * Open one question from a link elsewhere in HQ (`?open=<id>`: the bell, a
   * phone push, Meeting Detail → Client questions). It is read into the cache
   * first, so it opens even when it is not on the page this list shows (an
   * answered question, under the default Open filter). One that cannot be read
   * is said above the list, never opened blank.
   */
  async openById(id: number) {
    if (!Number.isInteger(id) || id <= 0) return;
    const ended = sessionMark(this.rootStore);
    const fresh = await this.reloadRecord(id);
    if (ended()) return;
    if (!fresh) {
      runInAction(() => {
        this.banner = `Question #${id} could not be opened: it does not exist, or HQ could not read it. Refresh to try again.`;
      });
      return;
    }
    this.select(id);
  }

  openForm(action: InquiryAction) {
    const r = this.selected;
    if (!r || !this.actions.includes(action)) return;
    const form = blankForm(action);
    if (action === "release" || action === "client-text") form.clientText = clientTextDraft(r);
    // A question the app cannot take has one way to go; otherwise the person says which.
    if (action === "release") form.channel = instanceOptionRefusal(r.options.length) ? "pdf" : null;
    this.form = form;
    this.actionError = null;
    this.notice = null;
  }

  closeForm() {
    this.form = null;
    this.actionError = null;
  }

  updateForm(patch: Partial<ActionForm>) {
    if (this.form) this.form = { ...this.form, ...patch };
  }

  updateClientText(patch: Partial<ClientText>) {
    // A changed text is one nobody has confirmed: a confirmation about the old words says nothing about the new.
    if (this.form?.clientText) this.form = { ...this.form, clientText: { ...this.form.clientText, ...patch }, othersConfirmed: false };
  }

  /** The person confirms the flagged words are not about the clients the API named. */
  setOthersConfirmed(confirmed: boolean) {
    if (this.form?.action === "release") this.form = { ...this.form, othersConfirmed: confirmed };
  }

  /** How the released question reached the client. The app is refused for a question it cannot take. */
  setChannel(channel: ReleaseChannel) {
    if (!this.form || this.form.action !== "release") return;
    if (channel === "instance" && this.appRefusal) return;
    this.form = { ...this.form, channel };
  }

  setOptionText(optionId: string, text: string) {
    const t = this.form?.clientText;
    if (t) this.updateClientText({ optionText: { ...t.optionText, [optionId]: text } });
  }

  toggleOption(optionId: string) {
    const f = this.form;
    const r = this.selected;
    if (!f || !r) return;
    const has = f.optionIds.includes(optionId);
    const optionIds = has
      ? f.optionIds.filter((id) => id !== optionId)
      : r.multi
        ? [...f.optionIds, optionId]
        : [optionId];
    this.form = { ...f, optionIds };
  }

  /** The body the API expects for the open form. */
  private bodyFor(f: ActionForm, r: InquiryRecord): Record<string, unknown> {
    const expect = { stage: r.stage, updatedAt: r.updatedAt };
    switch (f.action) {
      case "answer-internal":
        return { action: "answer", side: "internal", optionIds: f.optionIds, text: f.text.trim() || null, expect };
      case "answer-client":
        return {
          action: "answer",
          side: "client",
          optionIds: f.optionIds,
          text: f.text.trim() || null,
          author: { name: f.authorName.trim() || null, email: f.authorEmail.trim() || null },
          expect,
        };
      case "release":
        // Into their app: HQ puts it there as part of this tap (Decision 5). As
        // the PDF: the person says they sent it.
        return f.channel === "instance"
          ? {
              action: "release",
              delivery: "publish",
              clientText: f.clientText,
              acknowledgedOtherClients: f.othersConfirmed ? [...f.othersFlagged] : [],
              expect,
            }
          : {
              action: "release",
              delivery: "manual",
              ...(f.channel ? { channel: f.channel } : {}),
              clientText: f.clientText,
              detail: f.detail.trim() || null,
              expect,
            };
      case "client-text":
        return { action: "client-text", clientText: f.clientText, expect };
      case "withdraw":
        return { action: "withdraw", reason: f.reason.trim(), expect };
      case "assign":
        // Not a compare-and-set on the API: who answers is not a stage.
        return { action: "assign", assigneeEmail: f.assigneeEmail.trim() };
    }
  }

  async submit(): Promise<boolean> {
    const f = this.form;
    const r = this.selected;
    const raw = this.raw;
    if (!f || !r || !this.canSubmit) return false;
    if (!raw) {
      this.actionError = "The client-inquiries service is not available in this build.";
      return false;
    }
    this.submitting = true;
    this.actionError = null;
    // reloadRecord and load swallow their failures, a sign-out's included, so
    // each await below is followed by a check: a re-read that "failed" because
    // the person left must not close the next person's record or reload for them.
    const ended = sessionMark(this.rootStore);
    try {
      const result = (await raw.patch(r.id, this.bodyFor(f, r))) as { delivery?: { detail?: string } } | null;
      const reloaded = await this.reloadRecord(r.id);
      if (ended()) return true;
      runInAction(() => {
        this.form = null;
        // A release's delivery detail is already on the record ("Released … ·
        // <detail>"); repeating it as a notice says it twice. The other actions'
        // delivery notes (HQ cannot take a released question back) are news.
        this.notice =
          f.action === "release"
            ? null
            : f.action === "client-text"
              ? "The client's text is saved. Nothing has been sent to them."
              : (result?.delivery?.detail ?? null);
      });
      if (!reloaded) this.closeStale(`The action went through. ${STALE_AFTER_ACTION}`);
      await this.load();
      return true;
    } catch (err) {
      // Ended by a sign-out: no error, and no re-read in the next session.
      if (isSessionEnded(err)) return false;
      const e = err as { code?: number; data?: { reason?: string; missing?: string[]; otherClients?: unknown } };
      const message = messageOf(err, "The action failed.");
      // Someone else moved it first: show what it is now, never retry for them.
      if (e?.code === 409) {
        const reloaded = await this.reloadRecord(r.id);
        if (ended()) return false;
        runInAction(() => {
          this.form = null;
        });
        if (!reloaded) this.closeStale(`${message} ${STALE_AFTER_ACTION}`);
        await this.load();
        if (ended() || !reloaded) return false;
      }
      runInAction(() => {
        this.actionError = message;
        // The API names the other clients it found in the text; the person edits
        // them out or confirms them — the form stays open either way.
        const named = e?.data?.otherClients;
        if (this.form?.action === "release" && e?.data?.reason === "other-client" && Array.isArray(named)) {
          const flagged = named.filter((n): n is string => typeof n === "string");
          this.form = { ...this.form, othersFlagged: [...new Set([...this.form.othersFlagged, ...flagged])], othersConfirmed: false };
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  }

  /**
   * The record on screen could not be re-read, so its stage and its buttons may
   * be what it WAS. Close it rather than leave stale actions offered — the
   * open-stage list cannot be relied on to refresh a record that just left it —
   * and say why above the list (Codex review round 2).
   */
  private closeStale(message: string) {
    runInAction(() => {
      this.selectedId = null;
      this.form = null;
      this.actionError = null;
      this.notice = null;
      this.banner = message;
      this.delivery?.open(null);
    });
  }

  dismissBanner() {
    this.banner = null;
  }

  /**
   * Re-read one record through the ServiceStore so the cached row is current.
   * Says whether it worked: when it did not, what the dialog shows may be the
   * record as it WAS, and the person is told so rather than shown stale
   * actions as if they were current (Codex review).
   */
  private async reloadRecord(id: number): Promise<boolean> {
    try {
      const fresh = await this.service?.get(id, { throwOnError: true } as never);
      return fresh != null;
    } catch {
      return false;
    }
  }

  reset() {
    this.loadSeq += 1;
    this.pageIds = [];
    this.total = null;
    this.loading = false;
    this.loaded = false;
    this.error = null;
    this.mayRelease = false;
    this.mayAssign = false;
    this.stageFilter = this.mine ? "all" : "open";
    this.page = 0;
    this.selectedId = null;
    this.delivery?.open(null);
    this.form = null;
    this.submitting = false;
    this.actionError = null;
    this.notice = null;
    this.banner = null;
  }
}

export default InquiryQueueStore;
